import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdSlot } from '../components/AdSlot';
import { RecipeCard } from '../components/RecipeCard';
import { SearchBar } from '../components/SearchBar';
import { recipes } from '../data/recipes';
import { searchRecipes } from '../lib/search';
import { usePageMeta } from '../lib/meta';

export function RecipesPage() {
  usePageMeta({
    title: 'All Recipes',
    description:
      'The complete collection: all twenty peacock omelette recipes, searchable by name, ingredient, or occasion.',
    path: '/recipes',
  });

  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const results = useMemo(() => searchRecipes(recipes, query), [query]);

  const setQuery = (value: string) => {
    setParams(value.trim() ? { q: value } : {}, { replace: true });
  };

  // Split the grid around the mid-list ad without disturbing result order.
  const firstHalf = results.slice(0, 9);
  const secondHalf = results.slice(9);

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <header className="max-w-3xl">
        <h1 className="font-display text-3xl text-plume-950 sm:text-5xl">The Collection</h1>
        <p className="mt-4 text-lg leading-relaxed text-plume-800">
          Twenty peacock omelette recipes, maintained under a single editorial standard. Search by
          name, ingredient, or occasion. Begin with the Classic; end, when you are ready, with the
          Humble.
        </p>
      </header>

      <SearchBar
        value={query}
        onChange={setQuery}
        resultCount={results.length}
        className="mt-8 max-w-2xl"
      />

      {results.length > 0 ? (
        <>
          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {firstHalf.map((recipe) => (
              <RecipeCard key={recipe.slug} recipe={recipe} />
            ))}
          </div>
          {secondHalf.length > 0 && (
            <>
              <AdSlot slot="display" />
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {secondHalf.map((recipe) => (
                  <RecipeCard key={recipe.slug} recipe={recipe} />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-gloss">
          <p className="font-display text-xl text-plume-900">
            No recipe matches &ldquo;{query.trim()}&rdquo;.
          </p>
          <p className="mt-3 text-plume-700">
            Try a broader term: an ingredient, a technique, a national tradition. The collection
            rewards persistence.
          </p>
        </div>
      )}
    </div>
  );
}
